// src/layouts/TopBar/SubscriptionSelector.jsx
// *****************************************************************************
// IMPORTS
// *****************************************************************************
import React, { startTransition } from 'react';
import { Grid, Select, theme, Tooltip } from 'antd';
import { LockOutlined, SafetyCertificateOutlined, KeyOutlined } from '@ant-design/icons';
import utils from 'utils/utils';
import { setLocale } from 'utils/helpers';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import { getFlag } from 'utils/phone';

// *****************************************************************************
// CONSTANTS
// *****************************************************************************
const { useBreakpoint } = Grid;

const SELECT_STYLE = { width: 200, minWidth: 160, maxWidth: 200 };
const LABEL_STYLE = { display: 'flex', alignItems: 'center', gap: 8 };
const FLAG_STYLE = { width: 16, height: 12, borderRadius: 2, objectFit: 'cover', flexShrink: 0 };

// *****************************************************************************
// MAIN COMPONENT
// *****************************************************************************
const SubscriptionSelector = props => {
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const setSubscriptionStore = useZustand('auth', 'setSubscription');
  const storedSubscriptions = useZustand('auth', 'subscriptions');
  const storedSubscription = useZustand('auth', 'subscription');
  const storedCurrentPage = useZustand('module', 'currentPage');

  // 2. HOOKS & REFS
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes('md');

  // 3. LOCAL VARIABLES I
  const placeholder = `${translateX('select_a_organization')}`;

  // 6. HANDLERS
  const handleChange = value => {
    // startTransition marks as low-priority, allowing animation to render first
    startTransition(() => {
      setSubscriptionStore(value);
    });
  };

  // 7. LOCAL VARIABLES II
  // Disabled when inside a document page (layout === 'document')
  const isDisabled = storedCurrentPage?.layout === 'document';

  // 8. UI
  if (!storedSubscriptions || storedSubscriptions.length === 0) return null;

  const selectOptions = storedSubscriptions
    .map(s => ({ value: s._id, label: s.organization, subscription: s }))
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

  return (
    <Select
      showSearch
      placeholder={placeholder}
      disabled={isDisabled}
      style={SELECT_STYLE}
      value={storedSubscription?._id}
      options={selectOptions}
      onChange={handleChange}
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      labelRender={({ label, value }) => {
        const subscription = storedSubscriptions?.find(s => s._id === value);
        return (
          <span style={LABEL_STYLE}>
            {$getIcon(subscription, isDisabled)}
            {$getFlag(subscription)}
            <span>{label}</span>
          </span>
        );
      }}
      optionRender={option => (
        <span style={LABEL_STYLE}>
          {$getIcon(option.data.subscription)}
          {$getFlag(option.data.subscription)}
          <span>{option.data.label}</span>
        </span>
      )}
    />
  );

  // 9. INTERNAL FUNCTIONS
  function $getIcon(subscription, disabled = false) {
    if (!subscription) return null;

    return (
      <SubscriptionProfileIcon
        profile={subscription?.profile}
        showToolTip={!disabled}
        disabled={disabled}
      />
    );
  }

  function $getFlag(subscription) {
    const cc = subscription?.countryCode;
    if (!cc) return null;

    const src = getFlag(cc);
    if (!src) return null;

    return (
      <Tooltip title={translateX('origin')}>
        <img
          src={src}
          alt={cc}
          style={FLAG_STYLE}
          onError={e => {
            e.target.style.display = 'none';
          }}
        />
      </Tooltip>
    );
  }
};

// *****************************************************************************
// HELPERS
// *****************************************************************************
export const SubscriptionProfileIcon = ({ profile, showToolTip, disabled }) => {
  const { token } = theme.useToken();
  const disabledStyle = disabled ? { color: token.colorTextDisabled } : {};
  const disabledTwoTone = disabled ? token.colorTextDisabled : token.colorPrimary;

  const getIconAndTitle = () => {
    switch (profile) {
      case 'owner':
        return {
          icon: <SafetyCertificateOutlined size="small" twoToneColor={disabledTwoTone} />,
          title: setLocale('owner'),
        };
      case 'adm':
        return {
          icon: <KeyOutlined size="small" style={disabledStyle} />,
          title: setLocale('administrator'),
        };
      case 'keyUser':
        return {
          icon: <KeyOutlined size="small" style={disabledStyle} />,
          title: setLocale('key_user'),
        };
      case 'guest':
        return {
          icon: <LockOutlined size="small" style={disabledStyle} />,
          title: setLocale('guest'),
        };
      default:
        return {
          icon: <LockOutlined size="small" style={disabledStyle} />,
          title: setLocale('common_user'),
        };
    }
  };

  const { icon, title } = getIconAndTitle();

  if (showToolTip) {
    return <Tooltip title={title}>{icon}</Tooltip>;
  }

  // The original code adds a marginTop when there's no tooltip. I'll preserve that.
  return React.cloneElement(icon, { style: { ...icon.props.style, marginTop: 2 } });
};

// *****************************************************************************
// EXPORTS
// *****************************************************************************
export default SubscriptionSelector;
